/**
 * AdminCallUpdatesPage — review queue for the automatic funding-call sync.
 *
 * The sync updates the call registry by itself; the changes it detects (new
 * calls, deadline / budget / status changes, funder announcements) wait here.
 * Users are notified only about what is approved.
 */
import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Paper, Typography, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, Checkbox, Stack,
  Tooltip, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Link,
  ToggleButtonGroup, ToggleButton, Tabs, Tab, Badge,
} from '@mui/material';
import {
  Sync as SyncIcon, CheckCircle as ApproveIcon, Cancel as RejectIcon, People as PeopleIcon,
} from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import EmailOutboxDialog from './email/EmailOutboxDialog';
import BackButton from 'components/common/buttons/BackButton';
import CallDigestsPanel from './CallDigestsPanel';
import PageContainer from 'components/common/layout/PageContainer';

const KIND_COLORS = {
  new: 'primary', deadline: 'warning', opening: 'info', status: 'secondary',
  budget: 'success', announcement: 'error', closed: 'default',
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—');

function RunsTable({ runs }) {
  if (!runs?.length) return <Typography variant="body2" color="text.secondary">No sync runs yet.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Started</TableCell><TableCell>Source</TableCell><TableCell>Status</TableCell>
            <TableCell align="right">Fetched</TableCell><TableCell align="right">New</TableCell>
            <TableCell align="right">Changed</TableCell><TableCell align="right">Closed</TableCell>
            <TableCell align="right">Pending</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{fmt(r.started_at)}{r.dry_run && <Chip label="dry run" size="small" sx={{ ml: 1 }} />}</TableCell>
              <TableCell>{r.source}</TableCell>
              <TableCell>
                <Tooltip title={r.error || ''}>
                  <Chip size="small" label={r.status}
                        color={r.status === 'success' ? 'success' : r.status === 'failed' ? 'error' : 'default'} />
                </Tooltip>
              </TableCell>
              <TableCell align="right">{r.fetched}</TableCell>
              <TableCell align="right">{r.created_count}</TableCell>
              <TableCell align="right">{r.updated_count}</TableCell>
              <TableCell align="right">{r.closed_count}</TableCell>
              <TableCell align="right">{r.pending}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Approved changes are not emailed one by one: staff send every follower ONE personal
 * summary of all approved, unsent updates (their proposals first, with why each call
 * concerns them).
 */
function SendSummaryPanel({ refreshKey, onSent }) {
  const { data: unsent, mutate } = useSWR(['/funding/call-changes/unsent-summary/', refreshKey], ([url]) => fetcher(url));
  const [reviewing, setReviewing] = useState(false);

  const send = async (expected) => {
    const res = await axiosService.post('/funding/call-changes/send-updates/', { expected });
    onSent(`Sent ${res.data.emails} summary email(s) to ${res.data.users} user(s), covering ${res.data.changes} update(s).`);
    setReviewing(false);
    mutate();
  };

  const changes = unsent?.changes || 0;
  const users = unsent?.users || 0;
  return (
    <Alert
      severity={changes ? 'info' : 'success'}
      sx={{ mb: 3, alignItems: 'center' }}
      action={changes > 0 && (
        <Button size="small" variant="contained" onClick={() => setReviewing(true)} disabled={!users}>
          Review &amp; send update emails
        </Button>
      )}
    >
      {changes
        ? `${changes} approved update(s) not sent yet — ${users} user(s) will each get one personal summary email.`
        : 'All approved updates have been sent.'}

      <EmailOutboxDialog
        open={reviewing}
        onClose={() => setReviewing(false)}
        title="Call update emails"
        description={`One personal summary per user, covering the ${changes} approved update(s) that concern them. Their own proposals are listed first.`}
        csvName="call-update-emails.csv"
        loadOutbox={async () => (await axiosService.get('/funding/call-changes/outbox/')).data}
        loadPreview={async (email) => (await axiosService.get(`/funding/call-changes/summary-preview/?user=${email.key}`, {
          responseType: 'text', transformResponse: (r) => r,
        })).data}
        onSend={send}
      />
    </Alert>
  );
}

function CallChangesPanel() {
  const [reviewStatus, setReviewStatus] = useState('pending');
  const [kind, setKind] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);        // { decision, all }
  const [toast, setToast] = useState(null);
  const [summaryKey, setSummaryKey] = useState(0);

  const { data: summary, mutate: mutateSummary } = useSWR('/funding/call-changes/summary/', fetcher);
  const listUrl = `/funding/call-changes/?review_status=${reviewStatus}${kind ? `&kind=${kind}` : ''}`;
  const { data, error, isLoading, mutate } = useSWR(listUrl, fetcher);
  const changes = useMemo(() => data?.results || data || [], [data]);
  const total = data?.count ?? changes.length;
  const kinds = summary?.kinds || {};
  const pendingByKind = summary?.pending_by_kind || {};
  const isPending = reviewStatus === 'pending';

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOnPage = changes.length > 0 && changes.every((c) => selected.includes(c.id));

  const review = async ({ decision, all }) => {
    setBusy(true);
    try {
      const body = all ? { all: true, kind: kind || undefined, decision } : { ids: selected, decision };
      const res = await axiosService.post('/funding/call-changes/review/', body);
      const r = res.data;
      setToast(decision === 'approve'
        ? `Approved ${r.approved} update(s). Send them with “Send update emails” when you are done reviewing.`
        : `Rejected ${r.rejected} update(s). No one was notified.`);
      setSummaryKey((k) => k + 1);
      setSelected([]);
      mutate();
      mutateSummary();
    } catch (e) {
      setToast(e.response?.data?.error || 'Review failed.');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const scopeCount = kind ? (pendingByKind[kind] || 0) : (summary?.pending_total || 0);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Changes the sync detected in calls already on Otable (deadline, opening date, status, budget,
        funder announcements). Approving does not email anyone: when you are done, “Send update emails” sends
        each user who saved a call or has a proposal for it ONE personal summary of everything approved.
        New calls reach users through the personalised emails instead.
      </Typography>

      <SendSummaryPanel refreshKey={summaryKey} onSent={setToast} />

      <Typography variant="h6" fontWeight={700} mb={1}>Recent sync runs</Typography>
      <Box mb={4}><RunsTable runs={summary?.runs} /></Box>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <ToggleButtonGroup size="small" exclusive value={reviewStatus}
                           onChange={(_, v) => { if (v) { setReviewStatus(v); setSelected([]); } }}>
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="approved">Approved</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
          <ToggleButton value="silent">Silent</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip label="All kinds" onClick={() => { setKind(''); setSelected([]); }}
                color={!kind ? 'primary' : 'default'} variant={!kind ? 'filled' : 'outlined'} />
          {Object.entries(kinds).filter(([k]) => !['closed', 'new'].includes(k)).map(([k, label]) => (
            <Chip key={k} onClick={() => { setKind(k); setSelected([]); }}
                  label={isPending && pendingByKind[k] ? `${label} (${pendingByKind[k]})` : label}
                  color={kind === k ? KIND_COLORS[k] : 'default'} variant={kind === k ? 'filled' : 'outlined'} />
          ))}
        </Box>
      </Stack>

      {isPending && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
            {selected.length ? `${selected.length} selected` : 'Select updates below, or act on all pending ones.'}
          </Typography>
          <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />}
                  disabled={!selected.length || busy} onClick={() => setConfirm({ decision: 'approve', all: false })}>
            Approve selected
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<RejectIcon />}
                  disabled={!selected.length || busy} onClick={() => setConfirm({ decision: 'reject', all: false })}>
            Reject selected
          </Button>
          <Button size="small" color="success" disabled={!scopeCount || busy}
                  onClick={() => setConfirm({ decision: 'approve', all: true })}>
            Approve all {scopeCount}{kind ? ` × ${kinds[kind]}` : ''}
          </Button>
          <Button size="small" color="error" disabled={!scopeCount || busy}
                  onClick={() => setConfirm({ decision: 'reject', all: true })}>
            Reject all
          </Button>
        </Paper>
      )}

      {error && <Alert severity="error">Failed to load call updates.</Alert>}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : !changes.length ? (
        <Alert severity="success">Nothing here.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {isPending && (
                  <TableCell padding="checkbox">
                    <Checkbox checked={allOnPage} indeterminate={!allOnPage && selected.length > 0}
                              onChange={() => setSelected(allOnPage ? [] : changes.map((c) => c.id))} />
                  </TableCell>
                )}
                <TableCell>Change</TableCell>
                <TableCell>Call</TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="right">
                  <Tooltip title={isPending ? 'Users who would be notified' : 'Users notified'}><PeopleIcon fontSize="small" /></Tooltip>
                </TableCell>
                <TableCell>Detected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.map((c) => (
                <TableRow key={c.id} hover selected={selected.includes(c.id)}>
                  {isPending && (
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
                    </TableCell>
                  )}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip size="small" label={c.kind_label} color={KIND_COLORS[c.kind] || 'default'} />
                    {c.first_sync && (
                      <Tooltip title="First time this imported call was synced; the difference may come from the original import rather than a real change.">
                        <Chip size="small" label="first sync" variant="outlined" sx={{ ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 340 }}>
                    <Link component={RouterLink} to={`/funding/call/${c.call.id}/`} target="_blank" fontWeight={600} variant="body2">
                      {c.call.title}
                    </Link>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {c.call.code} · {c.call.program} · {c.call.status} · deadline {fmtDate(c.call.closes_at)}
                    </Typography>
                    {c.call.categories?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {c.call.categories.map((cat) => <Chip key={cat} label={cat} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />)}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {c.kind === 'new' ? 'New call from the source' : c.summary}
                    </Typography>
                    {c.call.external_url && (
                      <Link href={c.call.external_url} target="_blank" rel="noopener noreferrer" variant="caption">Source page ↗</Link>
                    )}
                  </TableCell>
                  <TableCell align="right">{c.audience}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="caption">{fmt(c.created)}</Typography>
                    {c.reviewed_by_name && (
                      <Typography variant="caption" color="text.secondary" display="block">by {c.reviewed_by_name}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {total > changes.length && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.5 }}>
              Showing the latest {changes.length} of {total}. “Approve all” / “Reject all” apply to all {scopeCount}.
            </Typography>
          )}
        </TableContainer>
      )}

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>{confirm?.decision === 'approve' ? 'Approve updates?' : 'Reject updates?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirm?.decision === 'approve'
              ? `This approves ${confirm?.all ? scopeCount : selected.length} update(s). Nobody is emailed yet — use “Send update emails” to send each user one summary of all approved updates.`
              : `This rejects ${confirm?.all ? scopeCount : selected.length} update(s). The registry keeps the synced data, but no one is notified.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={confirm?.decision === 'approve' ? 'success' : 'error'}
                  onClick={() => review(confirm)} disabled={busy}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
            {confirm?.decision === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}


export default function AdminCallUpdatesPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'changes' ? 'changes' : 'digests';
  const { data: summary } = useSWR('/funding/call-changes/summary/', fetcher);
  const { data: drafts } = useSWR('/funding/call-digests/?status=draft', fetcher);
  const draftCount = drafts?.count ?? drafts?.results?.length ?? 0;

  return (
    <PageContainer width="wide">
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <BackButton position="inline" />
        <SyncIcon color="primary" />
        <Typography variant="h4" fontWeight={800}>Call updates</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        The funding-call sync keeps the registry up to date automatically. Users are emailed or notified
        only about what you approve here.
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setParams({ tab: v })} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="digests" label={<Badge color="warning" badgeContent={draftCount} sx={{ pr: draftCount ? 1.5 : 0 }}>Personalised emails</Badge>} />
        <Tab value="changes" label={<Badge color="warning" badgeContent={summary?.pending_total || 0} sx={{ pr: summary?.pending_total ? 1.5 : 0 }}>Changes to calls</Badge>} />
      </Tabs>
      {tab === 'digests' ? <CallDigestsPanel /> : <CallChangesPanel />}
    </PageContainer>
  );
}
